// covered-by: frontends/carrie/src/services/__tests__/projects.test.ts
// covered-by: frontends/carrie/src/ask-v2/services/__tests__/reviews.test.ts
/**
 * Repair a citation this client cannot read, and only drop what cannot be saved.
 *
 * WHY THIS MODULE EXISTS (it is new): the treatment below was written on the
 * ask-v2 review path (`ask-v2/services/reviews.ts`), after one unreadable
 * citation blanked a live review. The ticket-comments path reads the SAME union
 * through `getTicketCommentsResponseSchema` and had no equivalent, so it threw
 * instead — and the React Router error boundary turned that throw into a blank
 * ticket view (58 prod events in the 24h to 2026-09-24, none in the nine days
 * before it). Both paths now call this one implementation, so they cannot drift
 * apart on what "unreadable" means.
 *
 * A citation is an ANNOTATION on a comment: `citations` is an array of a UNION,
 * so one unreadable entry fails its comment, which fails the review or the
 * ticket payload, which fails the whole response — and the caller throws, so a
 * review of seven findings, or a ticket with one bad chip, renders as an error.
 * Losing a citation must cost the chip, not the finding a reviewer has to act
 * on.
 *
 * This is the same trade christo already makes on the chat path, where
 * `warholChat.ts` safeParse-drops each warhol knowledge citation before
 * storing it. The two carrie read paths had no equivalent, and a rule citation
 * whose `location` was a sheet LABEL rather than the object this schema
 * declares blanked a live review on the warhol-review-kdb epic.
 *
 * Deliberately narrow: only `citations` entries are dropped. Anything else
 * malformed still throws, because a comment with no text or a review with no
 * id is corruption a silent recovery would hide.
 */
import { ticketMessageCitationSchema } from "@mg/schemas/src/commons/ticket";

/**
 * The citation with its unreadable FIELDS removed, or null if nothing is left.
 *
 * Dropping the whole record for one bad field costs far more than it saves. The
 * live case on the review path: a review comment carried a rule citation whose
 * `location` was the source SHEET LABEL where the schema declares an object,
 * while its `ruleId`, `statement`, `quote` and `sourceName` were all fine. A
 * record thrown away for that takes the rule chip AND the inline claim link
 * with it -- so the reader loses the citation entirely to save them from a
 * missing highlight.
 *
 * Driven by zod's own issue paths rather than a list of fields this file
 * guesses at: whatever the schema rejected is what gets removed, so a future
 * field is handled without being enumerated here. Required fields cannot be
 * repaired this way -- removing one only moves the error -- so the retry is
 * capped and a record that still fails is dropped as before.
 */
export function repairCitation(citation: unknown): unknown | null {
  if (ticketMessageCitationSchema.safeParse(citation).success) return citation;
  if (citation == null || typeof citation !== "object") return null;

  let candidate: Record<string, unknown> = {
    ...(citation as Record<string, unknown>),
  };
  // One pass per offending top-level key, bounded: each pass must strictly
  // shrink the object, so this cannot loop.
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parsed = ticketMessageCitationSchema.safeParse(candidate);
    if (parsed.success) return candidate;
    const offending = deletableKeys(parsed.error, candidate);
    if (offending.size === 0) return null;
    const next: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(candidate)) {
      if (!offending.has(key)) next[key] = value;
    }
    if (Object.keys(next).length === Object.keys(candidate).length) return null;
    candidate = next;
  }
  return null;
}

/**
 * The keys to remove to get `candidate` past the schema, or none.
 *
 * `ticketMessageCitationSchema` is a UNION, and a failed union reports one
 * `invalid_union` issue at the root with the real errors nested in
 * `unionErrors` -- so reading `issue.path` alone finds nothing to remove and
 * every repairable record looks unrepairable.
 *
 * Members whose complaints are about ABSENT keys are skipped: a file citation
 * rejecting a rule citation for having no `url` is not a field to delete, it is
 * the wrong member. What is left is the member this record was nearly valid
 * for, and the fewest deletions that satisfy it.
 */
function deletableKeys(
  error: { issues: unknown[] },
  candidate: Record<string, unknown>,
): Set<string> {
  type Issue = { path?: unknown[]; unionErrors?: { issues: Issue[] }[] };
  const issues = error.issues as Issue[];

  // The key a complaint is about, when we actually hold that key. A complaint
  // about an ABSENT key (a file citation objecting to a rule citation for
  // having no `url`) names nothing to remove, so it is not a held key.
  const heldKey = (issue: Issue): string | null => {
    const key = issue.path?.[0];
    return typeof key === "string" && key in candidate ? key : null;
  };

  const keysOf = (from: Issue[]): Set<string> => {
    const keys = new Set<string>();
    for (const issue of from) {
      const key = heldKey(issue);
      if (key !== null) keys.add(key);
    }
    return keys;
  };

  const direct = keysOf(issues);
  if (direct.size > 0) return direct;

  const members = issues.flatMap((issue) => issue.unionErrors ?? []);
  let best: Set<string> | null = null;
  for (const member of members) {
    // EVERY complaint must be about a key we hold, or removing them cannot help
    // and we would be guessing at the wrong union member.
    // Counted per COMPLAINT, not per distinct key: one `location` object with
    // two bad members (`pageNumber` AND `rel_x`) raises two complaints about the
    // single key that has to go. Comparing the distinct-key count to the raw
    // issue count skipped that member, and when it was the only near match
    // `best` stayed null and the whole citation was DROPPED -- losing a chip
    // that deleting `location` alone would have saved.
    if (!member.issues.every((issue) => heldKey(issue) !== null)) continue;
    const keys = keysOf(member.issues);
    if (keys.size === 0) continue;
    if (!best || keys.size < best.size) best = keys;
  }
  return best ?? new Set<string>();
}

/**
 * `holder` with its own `citations` array repaired, or `holder` itself when
 * every citation in it already parses.
 */
function withRepairedCitations(holder: unknown): unknown {
  if (holder == null || typeof holder !== "object") return holder;
  const citations = (holder as { citations?: unknown }).citations;
  if (!Array.isArray(citations)) return holder;

  const readable: unknown[] = [];
  let changed = false;
  for (const citation of citations) {
    const repaired = repairCitation(citation);
    if (repaired === null) {
      changed = true;
      continue;
    }
    if (repaired !== citation) changed = true;
    readable.push(repaired);
  }
  return changed ? { ...(holder as object), citations: readable } : holder;
}

/**
 * One comment with every unreadable citation repaired or dropped -- its own,
 * and those of every message nested under it.
 *
 * `messages` matters as much as the top level: `baseTicketCommentSchema`
 * declares `messages: z.array(baseTicketMessageSchema)` and
 * `askPunttReviewCommentSchema` declares `messages: z.array(...)` too, both
 * carrying the SAME `citations` union. Repairing only the comment's own array
 * left the identical failure reachable one level deeper -- a reply with one
 * unreadable citation still failed its message, its comment, and the response,
 * and blanked the view this module exists to keep rendering.
 */
function repairComment(comment: unknown): unknown {
  const withOwn = withRepairedCitations(comment);
  if (withOwn == null || typeof withOwn !== "object") return withOwn;

  const messages = (withOwn as { messages?: unknown }).messages;
  if (!Array.isArray(messages)) return withOwn;

  let changed = false;
  const repairedMessages = messages.map((message) => {
    const repaired = withRepairedCitations(message);
    if (repaired !== message) changed = true;
    return repaired;
  });
  return changed
    ? { ...(withOwn as object), messages: repairedMessages }
    : withOwn;
}

/**
 * The comments carrie parses ticket citations out of -- the array a ticket's
 * comment list returns, and the array a review's `comments` field holds -- with
 * every citation this client cannot read repaired, or dropped when it cannot be
 * repaired at all.
 *
 * Returns the input array unchanged (same reference) when there is nothing to
 * repair, so a healthy response costs one pass and allocates nothing.
 */
export function withReadableCitations(comments: unknown): unknown {
  if (!Array.isArray(comments)) return comments;

  let changed = false;
  const readable = comments.map((comment) => {
    const repaired = repairComment(comment);
    if (repaired !== comment) changed = true;
    return repaired;
  });
  return changed ? readable : comments;
}
